import { FormEvent, useCallback, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { browserName, deviceDetect, isDesktop } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { ELoginMethod, ELoginSource } from '@aqx-core/common/api/auth';
import { useBaseForm } from '@aqx-core/common/hooks/form/use-base-form';
import { ECommonResponseCode, mapResponseCodeToMessage } from '@aqx-core/common/request';
import { IOtpLoginFormParams } from '@aqx-core/common/services/auth';
import { authenticationViewController } from '@aqx-core/common/services/auth/controller';
import { EUISnackBarMessageType, snackbarController } from '@aqx-core/common/services/snackbar';

export const useOtpLoginForm = () => {
  const { t } = useTranslation();

  const schema = useMemo(() => {
    return yup.object().shape({
      userId: yup.string().required(t('Enter a valid email or phone number.')),
      dialCode: yup.string().optional(),
      phoneNumber: yup
        .string()
        .optional()
        .test('phoneNumber-digits-only', t('Enter a valid email or phone number.'), function (value) {
          // If no value, it's valid (optional field)
          if (!value) {
            return true;
          }
          // Must contain only digits
          return /^\d+$/.test(value);
        }),
      password: yup.string().required(() => t('Please enter a valid password.'))
    });
  }, [t]);

  const resolver = useMemo(() => yupResolver(schema), [schema]);

  const useFormMethods = useBaseForm<IOtpLoginFormParams>({
    resolver,
    mode: 'onChange',
    shouldFocusError: false
  });

  const { handleSubmit, trigger, watch } = useFormMethods;

  const [dialCode, phoneNumber] = watch(['dialCode', 'phoneNumber']);

  const createDynamicSubmit = useCallback(
    (loginType: ELoginMethod, onOtpError?: (errorMessage: string) => void) => {
      return (e?: FormEvent<HTMLFormElement>) => {
        e?.preventDefault();
        handleSubmit((values: IOtpLoginFormParams) => {
          const { userId, password } = values;
          authenticationViewController.login({
            userId,
            password,
            source: isDesktop ? ELoginSource.WEB : ELoginSource.MOBILE_WEB,
            browserType: browserName ?? undefined,
            deviceType: deviceDetect(window.navigator.userAgent)?.osName ?? undefined,
            loginType,
            errorCallback: (code: ECommonResponseCode, message: string) => {
              // Special handling for TW_1005 (Invalid OTP code)
              if (code === ECommonResponseCode.TW_1005 && onOtpError) {
                onOtpError(t('The code you entered is incorrect, please try again.'));
                return;
              }

              snackbarController.showAlert({
                message: mapResponseCodeToMessage(code as ECommonResponseCode, message),
                messageType: EUISnackBarMessageType.ERROR
              });
            }
          });
        })(e);
      };
    },
    [handleSubmit, t]
  );

  return {
    useFormMethods,
    createDynamicSubmit,
    trigger,
    dialCode,
    phoneNumber
  };
};
